import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { ProductVariant } from '../../interfaces/wheels';
import { WheelCartService, CartItem, CartValidationResult } from '../../services/wheel-cart.service';

@Component({
  selector: 'app-wheel-cart-manager',
  templateUrl: './wheel-cart-manager.component.html',
  styleUrls: ['./wheel-cart-manager.component.scss']
})
export class WheelCartManagerComponent implements OnInit, OnChanges {
  @Input() cartItems: CartItem[] = [];
  @Input() product: ProductVariant;
  @Input() isProcessing: boolean = false;
  @Input() isSearchBySize: boolean = false;
  @Input() checkfitment: boolean = false;
  
  // Customization inputs
  @Input() isProductCustomization: boolean = false;
  @Input() custom_binders: any[] = [];
  @Input() p_customization_submitted: boolean = false;
  
  @Output() onAddToCart = new EventEmitter<any>();
  @Output() onCustomizationRequired = new EventEmitter<any>();
  @Output() onValidationError = new EventEmitter<string>();

  // Cache validation results to prevent infinite loops
  private _isCustomizationValidCache: boolean | null = null;
  private _canAddToCartCache: boolean | null = null;
  private _lastValidationTimestamp: number = 0;
  private readonly VALIDATION_CACHE_DURATION = 100; // ms

  constructor(
    private wheelCartService: WheelCartService,
    private toastr: ToastrService
  ) { }
  ngOnInit(): void {
    // Component initialized
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Clear validation cache when inputs change
    if (changes['cartItems'] || changes['custom_binders'] || changes['isProductCustomization'] || 
        changes['checkfitment'] || changes['isSearchBySize']) {
      this.clearValidationCache();
    }
  }

  /**
   * Clear validation cache - call this when inputs change
   */
  private clearValidationCache(): void {
    this._isCustomizationValidCache = null;
    this._canAddToCartCache = null;
    this._lastValidationTimestamp = 0;
  }

  /**
   * Check if cache is still valid
   */
  private isCacheValid(): boolean {
    return (Date.now() - this._lastValidationTimestamp) < this.VALIDATION_CACHE_DURATION;
  }

  /**
   * Gets total quantity of all cart items
   */
  getTotalQuantity(): number {
    return this.wheelCartService.getTotalQuantity(this.cartItems);
  }

  /**
   * Gets cart items grouped by type for display
   */
  getCartSummary(): { type: string; quantity: number }[] {
    const summary: { [key: string]: number } = {};
    
    this.cartItems.forEach(item => {
      const type = item.type || 'wheels';
      summary[type] = (summary[type] || 0) + item.quantity;
    });

    return Object.keys(summary).map(type => ({
      type: type.charAt(0).toUpperCase() + type.slice(1),
      quantity: summary[type]
    }));
  }  /**
   * Handles add to cart button click
   */
  addToCart(): void {
    // Check if customization is required but not completed
    if (this.isProductCustomization && !this.isCustomizationValid()) {
      this.handleCustomizationValidation();
      return;
    }

    // Validate cart items
    const validation: CartValidationResult = this.wheelCartService.validateCartItems(this.cartItems);
    
    if (!validation.isValid) {
      const errorMessage = validation.errors.length > 0 
        ? validation.errors[0] 
        : "Please choose customization options properly";
      
      this.toastr.error(errorMessage, "Validation Error");
      this.onValidationError.emit(errorMessage);
      return;
    }

    // Additional validation for search-by-size
    if (this.isSearchBySize && !this.validateStaggeredSetup()) {
      return;
    }

    // Prepare cart data
    const sessionId = localStorage.getItem('session_id') || '';
    const cartData = this.wheelCartService.prepareCartData(this.cartItems, sessionId);

    // Emit add to cart event
    this.onAddToCart.emit(cartData);
  }/**
   * Validates if customization is properly completed
   */
  private isCustomizationValid(): boolean {
    // Use cached result if still valid
    if (this._isCustomizationValidCache !== null && this.isCacheValid()) {
      return this._isCustomizationValidCache;
    }

    if (!this.isProductCustomization || this.custom_binders.length === 0) {
      this._isCustomizationValidCache = true;
      this._lastValidationTimestamp = Date.now();
      return true; // No customization required
    }

    // Check if all required customization fields are filled
    const isValid = this.custom_binders.every(binder => {
      return !!(binder.custom_finish && 
             binder.bolt_pattern && 
             binder.offset && 
             binder.vehicle_year_make_model);
    });

    // Cache the result
    this._isCustomizationValidCache = isValid;
    this._lastValidationTimestamp = Date.now();
    
    return isValid;
  }

  /**
   * Handles customization validation errors
   */
  private handleCustomizationValidation(): void {
    // Mark as submitted to show validation errors
    this.p_customization_submitted = true;
    
    // Find which customization is missing
    const missingCustomization = this.custom_binders.findIndex(binder => {
      return !binder.custom_finish || 
             !binder.bolt_pattern || 
             !binder.offset || 
             !binder.vehicle_year_make_model;
    });

    let errorMessage = "Please complete all customization options";
    if (missingCustomization >= 0) {
      const wheelName = this.custom_binders.length > 1 
        ? (missingCustomization === 0 ? "front" : "rear") 
        : "";
      errorMessage = `Please complete ${wheelName} wheel customization options`;
    }

    this.toastr.error(errorMessage, "Customization Required");
    this.onValidationError.emit(errorMessage);
  }

  /**
   * Validates staggered wheel setup for search-by-size
   */
  private validateStaggeredSetup(): boolean {
    const frontItems = this.cartItems.filter(item => item.type === 'front');
    const rearItems = this.cartItems.filter(item => item.type === 'rear');
    
    // Must have at least front wheels
    if (frontItems.length === 0) {
      this.toastr.error("Please select front wheel size", "Validation Error");
      return false;
    }

    // If product has rear specifications, validate rear wheels
    if (this.product?.rear_size && rearItems.length === 0) {
      this.toastr.error("Please select rear wheel size for staggered setup", "Validation Error");
      return false;
    }

    return true;
  }
  /**
   * Gets the appropriate button text
   */
  getButtonText(): string {
    if (this.isProcessing) {
      return 'Adding...';
    }

    // If customization is required but not completed
    if (this.isProductCustomization && !this.isCustomizationValid()) {
      return 'Complete Customization First';
    }
    
    const totalQty = this.getTotalQuantity();
    if (totalQty > 0) {
      return `Add ${totalQty} wheel${totalQty > 1 ? 's' : ''} to Cart`;
    }
    
    if (this.cartItems.length === 0) {
      return 'Select Wheels';
    }
    
    return 'Add to Cart';
  }

  /**
   * Gets validation message for display
   */
  getValidationMessage(): string {
    // Customization-specific messages
    if (this.isProductCustomization && !this.isCustomizationValid()) {
      const missingCount = this.custom_binders.filter(binder => 
        !binder.custom_finish || !binder.bolt_pattern || !binder.offset || !binder.vehicle_year_make_model
      ).length;
      
      if (missingCount > 0) {
        return `Please complete customization for ${missingCount} wheel${missingCount > 1 ? 's' : ''}`;
      }
    }

    if (!this.canAddToCart()) {
      if (!this.isSearchBySize && !this.checkfitment) {
        return "Please check fitment first";
      }
      
      if (this.cartItems.length === 0) {
        return "Please select wheel size and quantity";
      }
      
      return "Please complete your selection";
    }
    
    return "";
  }
  /**
   * Checks if items can be added to cart
   */
  canAddToCart(): boolean {
    // Use cached result if still valid
    if (this._canAddToCartCache !== null && this.isCacheValid()) {
      return this._canAddToCartCache;
    }

    // Must have fitment check passed (for regular search) or be search-by-size
    if (!this.isSearchBySize && !this.checkfitment) {
      this._canAddToCartCache = false;
      this._lastValidationTimestamp = Date.now();
      return false;
    }

    // Must have valid cart items
    if (!this.wheelCartService.canAddToCart(this.cartItems)) {
      this._canAddToCartCache = false;
      this._lastValidationTimestamp = Date.now();
      return false;
    }

    // If customization is required, it must be completed
    if (this.isProductCustomization && !this.isCustomizationValid()) {
      this._canAddToCartCache = false;
      this._lastValidationTimestamp = Date.now();
      return false;
    }

    this._canAddToCartCache = true;
    this._lastValidationTimestamp = Date.now();
    return true;
  }
  /**
   * Determines if validation message should be shown
   */
  shouldShowValidationMessage(): boolean {
    return !this.canAddToCart() && !this.isProcessing;
  }

  /**
   * Checks if a customization binder is complete
   */
  isBinderComplete(binder: any): boolean {
    return !!(binder.custom_finish && 
              binder.bolt_pattern && 
              binder.offset && 
              binder.vehicle_year_make_model);
  }
}
